import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/common.css';
import '../../styles/test-plans.css';
import '../../styles/modal.css';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';
import { AccessibleSelect } from '../common';

const AVAILABLE_PANEL_HEIGHT = 'h-[480px]';
const SELECTED_PANEL_HEIGHT = 'h-[480px]';

const TestCasesListSkeleton = ({ showSelectAllPlaceholder = false }) => (
  <div className={`${AVAILABLE_PANEL_HEIGHT} overflow-hidden border rounded flex flex-col`} aria-busy="true" aria-live="polite">
    {showSelectAllPlaceholder && (
      <div className="p-2 bg-gray-50 border-b shrink-0">
        <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
      </div>
    )}
    <div className="flex-1 p-4 flex flex-col items-center justify-center gap-3 bg-gray-50/50 min-h-0">
      <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-gray-600">Loading test cases…</p>
      <div className="w-full max-w-sm space-y-3 mt-2 px-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex items-center gap-3 animate-pulse">
            <div className="h-4 w-4 rounded bg-gray-200 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 bg-gray-200 rounded" style={{ width: `${70 - i * 5}%` }} />
              <div className="h-2.5 bg-gray-100 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LinkTestCasesModal = ({
  isOpen,
  onClose,
  testSuite,
  projectId,
  onSuccess,
  mode = 'link'
}) => {
  const [testCases, setTestCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectedTestCasesData, setSelectedTestCasesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState('all');
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState(null);

  const resetModalState = () => {
    setTestCases([]);
    setSelectedCases([]);
    setSelectedTestCasesData([]);
    setLoading(true);
    setSubmitting(false);
    setErrors({});
    setStories([]);
    setSelectedStory('all');
    setStoryLoading(false);
    setStoryError(null);
  };

  const handleClose = () => {
    resetModalState();
    onClose && onClose();
  };

  const fetchStories = async () => {
    setStoryLoading(true);
    setStoryError(null);
    try {
      const res = await axiosInstance.get(`/jira_data/story_dropdown?project_id=${projectId}`);
      if (res.status === 'success' && Array.isArray(res.stories)) {
        setStories(res.stories);
      }
    } catch (err) {
      console.error('fetchStories error', err);
      setStoryError('Unable to fetch stories. Please try again.');
      toast.error('Failed to load stories');
    } finally {
      setStoryLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'link' && selectedStory === 'all') {
      fetchStories();
    }
    fetchTestCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, selectedStory]);

  const fetchTestCases = async () => {
    setLoading(true);
    setTestCases([]);
    try {
      const q = new URLSearchParams({ project_id: projectId, ...(selectedStory !== 'all' && { jira_story_id: selectedStory }) });
      const resp = await axiosInstance.get(`/test_case/test_case_dropdown?${q}`);
      const allCasesRaw = resp.test_cases || [];

      const normalizedAllCases = allCasesRaw.map(item => ({
        test_case_id: item.test_case_id ?? item.id ?? item.testCaseId,
        name: item.test_case_name ?? item.name ?? item.title ?? '',
        story_key: item.story_key ?? item.jira_story_key ?? item.storyKey ?? null
      }));

      const linkedResp = await axiosInstance.get(`/test_case/linked_test_cases_list?suite_id=${testSuite.suite_id}`);
      const linkedRaw = linkedResp.linked_test_cases || [];

      const linkedIds = linkedRaw.map(x => (typeof x === 'object' ? (x.test_case_id ?? x.id) : x));

      if (mode === 'unlink') {
        let linkedNormalized = [];
        if (linkedRaw.length > 0 && typeof linkedRaw[0] === 'object') {
          linkedNormalized = linkedRaw.map(i => ({
            test_case_id: i.test_case_id ?? i.id,
            name: i.test_case_name ?? i.name ?? '',
            story_key: i.story_key ?? null
          }));
        } else {
          linkedNormalized = normalizedAllCases.filter(tc => linkedIds.includes(tc.test_case_id));
        }

        if (linkedNormalized.length === 0) {
          toast.warning('This test suite has no linked test cases to unlink');
          handleClose();
          return;
        }

        setTestCases(linkedNormalized);
        setSelectedCases([]);
        setSelectedTestCasesData([]);
      } else {
        const unlinked = normalizedAllCases.filter(tc => !linkedIds.includes(tc.test_case_id));
        setTestCases(unlinked);
      }
    } catch (err) {
      console.error('fetchTestCases error', err);
      setErrors(prev => ({ ...prev, testCases: 'Failed to load test cases' }));
      toast.error('Failed to load test cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCaseSelection = (testCaseId) => {
    const found = testCases.find(tc => tc.test_case_id === testCaseId) || selectedTestCasesData.find(tc => tc.test_case_id === testCaseId);

    setSelectedCases(prevIds => {
      if (prevIds.includes(testCaseId)) {
        setSelectedTestCasesData(prev => prev.filter(t => t.test_case_id !== testCaseId));
        return prevIds.filter(id => id !== testCaseId);
      } else {
        if (found) setSelectedTestCasesData(prev => [...prev, found]);
        return [...prevIds, testCaseId];
      }
    });
  };

  const handleSelectAll = () => {
    const currentIds = testCases.map(tc => tc.test_case_id);
    const toAdd = currentIds.filter(id => !selectedCases.includes(id));
    if (toAdd.length === 0) {
      setSelectedCases(prev => prev.filter(id => !currentIds.includes(id)));
      setSelectedTestCasesData(prev => prev.filter(tc => !currentIds.includes(tc.test_case_id)));
      return;
    }

    setSelectedCases(prev => [...prev, ...toAdd]);
    setSelectedTestCasesData(prev => {
      const existingIds = new Set(prev.map(p => p.test_case_id));
      const newItems = testCases.filter(tc => toAdd.includes(tc.test_case_id) && !existingIds.has(tc.test_case_id));
      return [...prev, ...newItems];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      let endpoint;
      const payload = { suite_id: testSuite.suite_id };

      if (mode === 'link') {
        endpoint = '/test_case/map_case_to_suite';
        payload.test_case_ids = selectedTestCasesData.map(tc => tc.test_case_id);
      } else {
        endpoint = '/test_case/unlink-test-cases';
        payload.test_case_ids = selectedCases;
      }

      const res = await axiosInstance.post(endpoint, payload);
      if (res.status === 'success') {
        toast.success(mode === 'link' ? `${payload.test_case_ids.length} test case(s) linked successfully` : `${payload.test_case_ids.length} test case(s) unlinked successfully`);
        onSuccess && onSuccess();
        handleClose();
      }
    } catch (err) {
      console.error('submit error', err);
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const storyOptions = useMemo(
    () => [
      { value: 'all', label: 'All Stories' },
      ...stories.map((s) => ({
        value: String(s.id),
        label: s.story_key,
      })),
    ],
    [stories]
  );

  if (!isOpen) return null;

  const isBusy = loading || submitting;

  return (
    <div data-rt-modal-overlay className="link-test-cases-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div
        className="modal link-test-cases-modal-panel"
        style={{ minHeight: mode === 'link' ? '640px' : '560px' }}
      >
        <div className="modal-header shrink-0">
          <h2>{mode === 'link' ? 'Link Test Casesssss' : 'Unlink Test Cases'} - {testSuite?.suite_name}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="modal-content p-4 flex-1 min-h-0 overflow-y-auto" style={{ maxWidth: '100%' }}>
            {mode === 'unlink' && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-3">
                  <div className="text-red-500">⚠️</div>
                  <div>
                    <h4 className="text-red-600 font-medium">Warning: Unlinking Test Cases</h4>
                    <p className="text-red-700">You are about to unlink test cases from this test suite. This will remove the association but won't delete the test cases themselves.</p>
                  </div>
                </div>
              </div>
            )}

            <div className={`grid ${mode === 'link' ? 'grid-cols-2' : 'grid-cols-1'} gap-6`}>
              <div className="min-w-0 flex flex-col">
                {mode === 'link' && (
                  <div className="mb-4">
                    <label
                      id="filter-by-story-label"
                      htmlFor="filter-by-story"
                      className="block text-sm font-medium mb-2"
                    >
                      Filter by Story
                    </label>
                    <AccessibleSelect
                      id="filter-by-story"
                      value={selectedStory}
                      onChange={setSelectedStory}
                      options={storyOptions}
                      placeholder="All Stories"
                      disabled={storyLoading || loading}
                      aria-labelledby="filter-by-story-label"
                    />
                    {storyError && <p className="text-sm text-red-600 mt-2">{storyError}</p>}
                  </div>
                )}

                <div className="mb-3 text-sm text-gray-600">{selectedCases.length} of {loading ? '…' : testCases.length} test cases selected</div>
                <label className="block font-medium mb-2">{mode === 'link' ? 'Available Test Cases' : 'Currently Linked Test Cases'}</label>

                {loading ? (
                  <TestCasesListSkeleton showSelectAllPlaceholder={mode === 'link'} />
                ) : testCases.length === 0 ? (
                  <div className={`${AVAILABLE_PANEL_HEIGHT} border rounded flex items-center justify-center text-gray-500`}>
                    {mode === 'link' ? 'No test cases available' : 'No test cases linked'}
                  </div>
                ) : (
                  <div className={`${AVAILABLE_PANEL_HEIGHT} border rounded overflow-hidden flex flex-col`}>
                    {mode === 'link' && (
                      <div className="p-2 bg-gray-50 border-b shrink-0">
                        <button type="button" className="btn" onClick={handleSelectAll} disabled={submitting}>{selectedCases.length === testCases.length ? 'Deselect All' : 'Select All'}</button>
                      </div>
                    )}
                    <div className="flex-1 min-h-0 overflow-y-auto divide-y">
                      {testCases.map(tc => (
                        <div key={tc.test_case_id} className={`p-3 cursor-pointer flex items-center justify-between ${selectedCases.includes(tc.test_case_id) ? 'bg-primary-50' : ''}`} onClick={() => handleCaseSelection(tc.test_case_id)}>
                          <div>
                            <div className="font-medium">{tc.name}</div>
                            {tc.story_key && <div className="text-xs text-gray-500">{tc.story_key}</div>}
                          </div>
                          {selectedCases.includes(tc.test_case_id) && (
                            <svg className="h-5 w-5 text-primary-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {errors.testCases && <div className="text-sm text-red-600 mt-2">{errors.testCases}</div>}
              </div>

              {mode === 'link' && (
                <div className="min-w-0 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Selected Test Cases</h3>
                    {selectedTestCasesData.length > 0 && (
                      <button type="button" className="text-sm text-red-600" disabled={submitting} onClick={() => { setSelectedCases([]); setSelectedTestCasesData([]); }}>Clear All</button>
                    )}
                  </div>
                  <div className={`${SELECTED_PANEL_HEIGHT} border rounded overflow-hidden`}>
                    <div className="h-full overflow-y-auto divide-y">
                      {selectedTestCasesData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-gray-500">No test cases selected</div>
                      ) : (
                        selectedTestCasesData.map(tc => (
                          <div key={tc.test_case_id} className="p-3 flex items-center justify-between">
                            <div>
                              <div className="font-medium">{tc.name} {tc.story_key ? <span className="text-xs text-gray-500">({tc.story_key})</span> : null}</div>
                            </div>
                            <button type="button" className="text-gray-400" disabled={submitting} onClick={(e) => { e.stopPropagation(); handleCaseSelection(tc.test_case_id); }}>
                              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {(mode === 'unlink' && selectedCases.length === 0 && !loading) && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-yellow-700">Please select the test cases you want to unlink from this test suite.</p>
              </div>
            )}
          </div>

          <div className="modal-footer shrink-0">
            {errors.submit && <div className="error-message">{errors.submit}</div>}
            <button type="button" className="btn secondary" onClick={handleClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={`btn ${mode === 'unlink' ? 'danger' : 'primary'}`} disabled={mode === 'link' ? (isBusy || selectedTestCasesData.length === 0) : (isBusy || selectedCases.length === 0)}>
              {submitting ? (mode === 'link' ? 'Linking...' : 'Unlinking...') : (mode === 'link' ? `Link ${selectedTestCasesData.length} Test Case(s)` : `Unlink ${selectedCases.length} Test Case(s)`)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkTestCasesModal;